import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ServerOff } from 'lucide-react';
import { type ShadowSystem } from '@bliks-io/contracts';
import { Page, PageHeader, PageContent, DataTable, Skeleton } from '@bliks-io/ui';
import { useShadowSystems } from '@/api/queries/useShadowSystems';
import { useUserTombstoneLookup } from '@/api/queries/useOrgMembers';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { SystemTabsNav } from '@/components/systems/SystemTabsNav';
import { ContentHeader } from '@/components/content-header';
import { createShadowSystemColumns } from './components/shadow-system-columns';
import { ShadowResolveDialog } from './components/ShadowResolveDialog';

function StatCard({
  label,
  value,
  isLoading
}: {
  label: string;
  value: number;
  isLoading: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card px-5 py-4">
      {isLoading ? (
        <Skeleton className="h-8 w-12" />
      ) : (
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
      )}
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export function ShadowSystemsListPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.shadowSystems');
  const { data: response, isLoading } = useShadowSystems();
  const userLookup = useUserTombstoneLookup();

  const [resolving, setResolving] = useState<ShadowSystem | null>(null);

  const columns = useMemo(
    () => createShadowSystemColumns(t, setResolving, userLookup),
    [t, userLookup]
  );

  const shadowSystems = response?.data ?? [];

  return (
    <Page>
      <PageHeader>
        <SystemTabsNav />
      </PageHeader>
      <PageContent>
        <ContentHeader
          icon={ServerOff}
          title={t('shadowSystems.title')}
          description={t('shadowSystems.description')}
        />

        <div className="mt-6 flex flex-wrap gap-4">
          <StatCard
            label={t('shadowSystems.stats.shadowSystems')}
            value={shadowSystems.length}
            isLoading={isLoading}
          />
          <StatCard
            label={t('shadowSystems.stats.recordingsAffected')}
            value={response?.totalRecordings ?? 0}
            isLoading={isLoading}
          />
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={shadowSystems}
            isLoading={isLoading}
            emptyMessage={t('shadowSystems.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('shadowSystems.search')}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="shadow-systems"
          />
        </div>

        <ShadowResolveDialog
          open={!!resolving}
          shadowSystem={resolving}
          onOpenChange={(o) => {
            if (!o) setResolving(null);
          }}
        />
      </PageContent>
    </Page>
  );
}
